import { IonIcon } from '@ionic/react';
import { arrowBack, folderOutline, warningOutline } from 'ionicons/icons';
import React from 'react';
import { useIcons } from '../../hooks/useIcons';
import { formatBytes } from '../../utils/memoryMonitor';
import { useCanvas } from '../canvas/CanvasContext';
import { AgentDuplicateInsights } from '../../types/AgentDuplicates';
import './DuplicateStatsCard.scss';

const basename = (folderPath: string): string => {
  const parts = folderPath.split(/[/\\]/).filter(Boolean);
  return parts[parts.length - 1] || folderPath;
};

interface DuplicateStatsCardProps {
  insights: AgentDuplicateInsights;
}

export const DuplicateStatsCard: React.FC<DuplicateStatsCardProps> = ({
  insights,
}) => {
  const { icons } = useIcons();
  const canvas = useCanvas();

  const openCleaner = () => {
    canvas?.openCanvas({
      id: 'duplicates',
      kind: 'duplicates',
      title: 'Duplicates',
    });
  };

  return (
    <div className="agent-duplicate-card">
      <div className="agent-duplicate-card-header">
        {icons.flameLogo ? (
          <img src={icons.flameLogo} alt="" />
        ) : (
          <IonIcon icon={folderOutline} />
        )}
        <div>
          <strong>Duplicate library scan</strong>
          <p>Counts are for your whole imported library.</p>
        </div>
      </div>

      <div className="agent-duplicate-stats">
        <div className="agent-duplicate-stat">
          <span className="agent-duplicate-stat-value">
            {insights.groupCount.toLocaleString()}
          </span>
          <span className="agent-duplicate-stat-label">songs with copies</span>
        </div>
        <div className="agent-duplicate-stat">
          <span className="agent-duplicate-stat-value">
            {insights.redundantFileCount.toLocaleString()}
          </span>
          <span className="agent-duplicate-stat-label">
            files you could remove
          </span>
        </div>
        <div className="agent-duplicate-stat">
          <span className="agent-duplicate-stat-value">
            {insights.reclaimableSpace}
          </span>
          <span className="agent-duplicate-stat-label">space to reclaim</span>
        </div>
      </div>

      {insights.unmeasuredFiles > 0 && (
        <p className="agent-duplicate-note">
          <IonIcon icon={warningOutline} />
          {insights.unmeasuredFiles} copy
          {insights.unmeasuredFiles === 1 ? '' : 'ies'} could not be measured —
          usually an offline drive. The space figure only covers what could be
          read.
        </p>
      )}

      {insights.topFolders.length > 0 && (
        <div className="agent-duplicate-folders">
          <span className="agent-duplicate-folders-title">Top folders</span>
          <ul>
            {insights.topFolders.slice(0, 3).map((folder) => (
              <li key={folder.folderPath}>
                <span
                  className="agent-duplicate-folder-name"
                  title={folder.folderPath}
                >
                  {basename(folder.folderPath)}
                </span>
                <span className="agent-duplicate-folder-meta">
                  {folder.redundantFiles} file
                  {folder.redundantFiles === 1 ? '' : 's'}
                  {folder.reclaimableBytes > 0
                    ? ` · ${formatBytes(folder.reclaimableBytes)}`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {canvas && (
        <button
          type="button"
          className="agent-duplicate-open"
          onClick={openCleaner}
        >
          <IonIcon icon={arrowBack} className="agent-duplicate-open-arrow" />
          Open duplicate cleaner
        </button>
      )}
    </div>
  );
};

export default DuplicateStatsCard;
